/**
 * Hook pour le header de bienvenue
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { WELCOME_MESSAGES, FALLBACK_GREETING, DEFAULT_USER_NAME } from '../../constants/landing/welcome.constants'

export function useWelcomeHeader() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentMessage, setCurrentMessage] = useState('')
  const [isChanging, setIsChanging] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const messageRef = useRef<HTMLSpanElement>(null)

  const greetingBuckets = useMemo(() => WELCOME_MESSAGES, [])

  // Messages d'accueil selon l'heure avec plus de variété
  const getWelcomeMessage = useCallback(() => {
    const hour = new Date().getHours()
    const day = new Date().getDay()
    let messagePool: readonly string[] = []

    if (day === 0 || day === 6) {
      messagePool = greetingBuckets.weekend
    } else if (hour >= 5 && hour < 12) {
      messagePool = greetingBuckets.morning
    } else if (hour >= 12 && hour < 17) {
      messagePool = greetingBuckets.afternoon
    } else if (hour >= 17 && hour < 22) {
      messagePool = greetingBuckets.evening
    } else {
      messagePool = greetingBuckets.night
    }

    if (!messagePool.length) {
      return FALLBACK_GREETING
    }

    return messagePool[Math.floor(Math.random() * messagePool.length)] ?? FALLBACK_GREETING
  }, [greetingBuckets])

  // Fonction pour changer le message avec morphing
  const changeMessageWithMorphing = useCallback(() => {
    if (isChanging) return

    setIsChanging(true)

    // Ajouter classe morphing
    if (messageRef.current) {
      messageRef.current.classList.add('morphing')
    }

    // Changer le message après l'animation de sortie
    setTimeout(() => {
      setCurrentMessage(getWelcomeMessage())
    }, 300)

    // Retirer la classe après l'animation complète
    setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.classList.remove('morphing')
      }
      setIsChanging(false)
    }, 700)
  }, [isChanging, getWelcomeMessage])

  // Effet parallax au scroll
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const scrollY = window.scrollY
        const parallaxValue = scrollY * 0.3 // Effet subtil
        headerRef.current.style.setProperty('--scroll-y', `${parallaxValue}px`)

        if (scrollY > 50) {
          headerRef.current.classList.add('parallax')
        } else {
          headerRef.current.classList.remove('parallax')
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Définir le message initial
    setCurrentMessage(getWelcomeMessage())

    // Changer le message automatiquement (toutes les 8 minutes)
    const messageInterval = setInterval(() => {
      changeMessageWithMorphing()
    }, 480000)

    return () => {
      clearInterval(messageInterval)
    }
  }, [getWelcomeMessage, changeMessageWithMorphing])

  // Navigation vers la Landing Page au clic sur le logo
  const handleLogoClick = () => {
    // Vibration tactile sur mobile
    if (navigator.vibrate) {
      navigator.vibrate(50)
    }

    // Rediriger vers la Landing Page
    navigate('/landing')
  }

  const handleMessageClick = () => {
    // Vibration tactile sur mobile
    if (navigator.vibrate) {
      navigator.vibrate(30)
    }

    // Changer le message
    changeMessageWithMorphing()
  }

  // Obtenir le nom d'affichage : nom complet ou email, sinon valeur par défaut
  const displayName = useMemo(() => {
    if (!user) return DEFAULT_USER_NAME
    
    // Construire le nom complet si firstName et lastName sont disponibles
    const fullName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    
    // Retourner le nom complet, sinon l'email, sinon la valeur par défaut
    return fullName || user.email || DEFAULT_USER_NAME
  }, [user])

  return {
    currentMessage,
    isChanging,
    headerRef,
    messageRef,
    handleLogoClick,
    handleMessageClick,
    displayName
  }
}

